// daisyUI component catalogue for the /design "Components" tab.
//
// Each entry lists the *style* variants daisyUI offers for that component, plus
// the colour and size suffixes it supports. The chosen combination is stored in
// the theme document (`styles`) and published, so the game markup can apply it:
//
//   <button class={styles.button}>…</button>
//
// `preview(classes)` returns the HTML for a specimen rendered with exactly the
// classes the game would use.

import type { StyleChoice } from "./theme";

export type { StyleChoice };

export interface ComponentVariant {
  id: string;
  label: string;
  /** Extra classes for this option. */
  cls: string;
  /** Optional hint shown under the option in the picker. */
  note?: string;
}

/** One chip inside an extra option group (card padding, background, …). */
export interface ComponentChoice {
  id: string;
  label: string;
  /** Classes appended to the published class list. */
  cls: string;
  /** Optional inline CSS custom properties this choice sets. */
  vars?: Record<string, string>;
}

/** An extra picker row beyond style/shape/colour/size. */
export interface OptionGroup {
  id: string;
  label: string;
  choices: ComponentChoice[];
  /** Choice id selected when the theme stores nothing (default: first choice). */
  default?: string;
  note?: string;
}

/** A free-text background image URL field (currently card only). */
export interface ImageField {
  label: string;
  placeholder: string;
  /** Option group + choice id that switches the image on. */
  group: string;
  choice: string;
}

export interface ComponentSpec {
  id: string;
  label: string;
  group: string;
  /** Base daisyUI class, also the default prefix for colour/size suffixes. */
  base: string;
  /** Prefix for the colour suffix when it differs from `base` (chat-bubble, step, …). */
  colorBase?: string;
  /** Prefix for the size suffix when it differs from `base`. */
  sizeBase?: string;
  /** Tone: solid / soft / outline / dash / ghost / link. */
  variants: ComponentVariant[];
  /** Structure: text, horizontal, square, circle, wide, … */
  shapes?: ComponentVariant[];
  colors: string[];
  sizes: string[];
  /** Extra picker rows (padding, margin, border, background, image, …). */
  optionGroups?: OptionGroup[];
  /** Free-text background image URL input shown under the option groups. */
  imageField?: ImageField;
  preview: (classes: string, variantId: string) => string;
  /** Used when the theme has no stored choice for this component. */
  defaults?: Partial<StyleChoice>;
  note?: string;
}

const COLORS = [
  "primary",
  "secondary",
  "accent",
  "neutral",
  "info",
  "success",
  "warning",
  "error",
];

const SIZES = ["xs", "sm", "md", "lg", "xl"];

export const COMPONENTS: ComponentSpec[] = [
  {
    id: "button",
    label: "Button",
    group: "Actions",
    base: "btn",
    variants: [
      { id: "solid", label: "Solid", cls: "" },
      { id: "soft", label: "Soft", cls: "btn-soft" },
      { id: "outline", label: "Outline", cls: "btn-outline" },
      { id: "dash", label: "Dash", cls: "btn-dash" },
      { id: "ghost", label: "Ghost", cls: "btn-ghost" },
      { id: "link", label: "Link", cls: "btn-link" },
    ],
    shapes: [
      { id: "default", label: "Default", cls: "" },
      { id: "wide", label: "Wide", cls: "btn-wide" },
      { id: "block", label: "Block", cls: "btn-block" },
      { id: "square", label: "Square", cls: "btn-square" },
      { id: "circle", label: "Circle", cls: "btn-circle" },
    ],
    colors: COLORS,
    sizes: SIZES,
    defaults: { color: "primary" },
    preview: (c, v) =>
      v === "circle"
        ? `<button class="${c}" aria-label="Play">▶</button>`
        : `<button class="${c}">Play</button>`,
  },
  {
    id: "input",
    label: "Input",
    group: "Data input",
    base: "input",
    variants: [
      { id: "default", label: "Bordered", cls: "" },
      { id: "ghost", label: "Ghost", cls: "input-ghost" },
    ],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) => `<input class="${c}" placeholder="nickname" />`,
    optionGroups: [
      {
        id: "width",
        label: "Width",
        choices: [
          { id: "auto", label: "Auto", cls: "" },
          { id: "xs", label: "XS", cls: "w-24" },
          { id: "sm", label: "SM", cls: "w-40" },
          { id: "md", label: "MD", cls: "w-56" },
          { id: "lg", label: "LG", cls: "w-80" },
          { id: "full", label: "Full", cls: "w-full" },
        ],
        default: "auto",
        note: "Fixed widths use rem utilities; Full fills whatever contains it.",
      },
      {
        id: "height",
        label: "Height",
        choices: [
          { id: "auto", label: "Auto", cls: "" },
          { id: "sm", label: "SM", cls: "h-8 min-h-0" },
          { id: "md", label: "MD", cls: "h-10 min-h-0" },
          { id: "lg", label: "LG", cls: "h-12 min-h-0" },
        ],
        default: "auto",
      },
      {
        id: "pad",
        label: "Padding",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "none", label: "None", cls: "px-0 py-0" },
          { id: "sm", label: "SM", cls: "px-2 py-1" },
          { id: "md", label: "MD", cls: "px-3 py-2" },
          { id: "lg", label: "LG", cls: "px-5 py-3" },
        ],
        default: "theme",
      },
      {
        id: "margin",
        label: "Margin",
        choices: [
          { id: "none", label: "None", cls: "" },
          { id: "sm", label: "SM", cls: "m-1" },
          { id: "md", label: "MD", cls: "m-2" },
          { id: "lg", label: "LG", cls: "m-4" },
        ],
        default: "none",
      },
      {
        id: "bdw",
        label: "Border width",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "thin", label: "1px", cls: "border" },
          { id: "thick", label: "2px", cls: "border-2" },
          { id: "none", label: "None", cls: "border-0" },
        ],
        default: "theme",
      },
      {
        id: "bds",
        label: "Border style",
        choices: [
          { id: "solid", label: "Solid", cls: "" },
          { id: "dashed", label: "Dashed", cls: "border-dashed" },
          { id: "dotted", label: "Dotted", cls: "border-dotted" },
        ],
        default: "solid",
      },
      {
        id: "radius",
        label: "Radius",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "none", label: "None", cls: "rounded-none" },
          { id: "sm", label: "SM", cls: "rounded-sm" },
          { id: "md", label: "MD", cls: "rounded-md" },
          { id: "lg", label: "LG", cls: "rounded-lg" },
          { id: "full", label: "Full", cls: "rounded-full" },
        ],
        default: "theme",
        note: "Theme follows the Radius knobs; the others pin this component.",
      },
    ],
  },
  {
    id: "select",
    label: "Select",
    group: "Data input",
    base: "select",
    variants: [
      { id: "default", label: "Bordered", cls: "" },
      { id: "ghost", label: "Ghost", cls: "select-ghost" },
    ],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) => `<select data-cycle="off" class="${c}"><option>Options</option></select>`,
    optionGroups: [
      {
        id: "width",
        label: "Width",
        choices: [
          { id: "auto", label: "Auto", cls: "" },
          { id: "xs", label: "XS", cls: "w-24" },
          { id: "sm", label: "SM", cls: "w-40" },
          { id: "md", label: "MD", cls: "w-56" },
          { id: "lg", label: "LG", cls: "w-80" },
          { id: "full", label: "Full", cls: "w-full" },
        ],
        default: "auto",
        note: "Fixed widths use rem utilities; Full fills whatever contains it.",
      },
      {
        id: "height",
        label: "Height",
        choices: [
          { id: "auto", label: "Auto", cls: "" },
          { id: "sm", label: "SM", cls: "h-8 min-h-0" },
          { id: "md", label: "MD", cls: "h-10 min-h-0" },
          { id: "lg", label: "LG", cls: "h-12 min-h-0" },
        ],
        default: "auto",
      },
      {
        id: "pad",
        label: "Padding",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "none", label: "None", cls: "px-0 py-0" },
          { id: "sm", label: "SM", cls: "px-2 py-1" },
          { id: "md", label: "MD", cls: "px-3 py-2" },
          { id: "lg", label: "LG", cls: "px-5 py-3" },
        ],
        default: "theme",
      },
      {
        id: "margin",
        label: "Margin",
        choices: [
          { id: "none", label: "None", cls: "" },
          { id: "sm", label: "SM", cls: "m-1" },
          { id: "md", label: "MD", cls: "m-2" },
          { id: "lg", label: "LG", cls: "m-4" },
        ],
        default: "none",
      },
      {
        id: "bdw",
        label: "Border width",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "thin", label: "1px", cls: "border" },
          { id: "thick", label: "2px", cls: "border-2" },
          { id: "none", label: "None", cls: "border-0" },
        ],
        default: "theme",
      },
      {
        id: "bds",
        label: "Border style",
        choices: [
          { id: "solid", label: "Solid", cls: "" },
          { id: "dashed", label: "Dashed", cls: "border-dashed" },
          { id: "dotted", label: "Dotted", cls: "border-dotted" },
        ],
        default: "solid",
      },
      {
        id: "radius",
        label: "Radius",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "none", label: "None", cls: "rounded-none" },
          { id: "sm", label: "SM", cls: "rounded-sm" },
          { id: "md", label: "MD", cls: "rounded-md" },
          { id: "lg", label: "LG", cls: "rounded-lg" },
          { id: "full", label: "Full", cls: "rounded-full" },
        ],
        default: "theme",
        note: "Theme follows the Radius knobs; the others pin this component.",
      },
    ],
  },
  {
    id: "cycle",
    label: "Cycle select",
    group: "Data input",
    base: "cycle-select",
    colorBase: "cycle",
    sizeBase: "cycle",
    variants: [
      { id: "boxed", label: "Boxed", cls: "" },
      { id: "pill", label: "Pill", cls: "cycle-pill" },
      { id: "underline", label: "Underline", cls: "cycle-underline" },
      { id: "bare", label: "Bare", cls: "cycle-bare" },
    ],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) =>
      `<div class="${c}">` +
      `<span class="cycle-step">‹</span>` +
      `<span class="cycle-value">Wipeout</span>` +
      `<span class="cycle-step">›</span>` +
      `</div>`,
    optionGroups: [
      {
        id: "width",
        label: "Width",
        choices: [
          { id: "context", label: "Context", cls: "" },
          { id: "auto", label: "Auto", cls: "cycle-w-auto" },
          { id: "w8", label: "8rem", cls: "cycle-w-8" },
          { id: "w12", label: "12rem", cls: "cycle-w-12" },
          { id: "w18", label: "18rem", cls: "cycle-w-18" },
          { id: "w24", label: "24rem", cls: "cycle-w-24" },
          { id: "full", label: "100%", cls: "cycle-w-full" },
        ],
        default: "context",
        note: "Context follows the surrounding row (saved-profile rows set their own widths, sound rows fill). Fixed widths cap at 100% of the container.",
      },
      {
        id: "bg",
        label: "Background",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "transparent", label: "Transparent", cls: "cycle-bg-transparent" },
          { id: "sunken", label: "Sunken", cls: "cycle-bg-sunken" },
          { id: "raised", label: "Raised", cls: "cycle-bg-raised" },
        ],
        default: "theme",
      },
      {
        id: "radius",
        label: "Radius",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "none", label: "None", cls: "cycle-r-none" },
          { id: "sm", label: "SM", cls: "cycle-r-sm" },
          { id: "md", label: "MD", cls: "cycle-r-md" },
          { id: "lg", label: "LG", cls: "cycle-r-lg" },
          { id: "full", label: "Full", cls: "cycle-r-full" },
        ],
        default: "theme",
        note: "Theme follows the Radius knobs; the others pin this component.",
      },
    ],
    note: "The ‹ value › stepper that replaces every dropdown in the menu (mode presets, saved profiles, sound files, …). The arrow buttons cycle the hidden native select.",
  },
  {
    id: "checkbox",
    label: "Checkbox",
    group: "Data input",
    base: "checkbox",
    variants: [{ id: "default", label: "Square", cls: "" }],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) => `<input type="checkbox" class="${c}" checked />`,
  },
  {
    id: "radio",
    label: "Radio",
    group: "Data input",
    base: "radio",
    variants: [{ id: "default", label: "Round", cls: "" }],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) => `<input type="radio" class="${c}" checked />`,
  },
  {
    id: "toggle",
    label: "Toggle",
    group: "Data input",
    base: "toggle",
    variants: [{ id: "default", label: "Switch", cls: "" }],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) => `<input type="checkbox" class="${c}" checked />`,
  },
  {
    id: "range",
    label: "Range / slider",
    group: "Data input",
    base: "range",
    variants: [{ id: "default", label: "Track", cls: "" }],
    colors: COLORS,
    sizes: SIZES,
    defaults: { color: "primary" },
    preview: (c) => `<input type="range" class="${c}" value="60" min="0" max="100" />`,
    optionGroups: [
      {
        id: "width",
        label: "Width",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "xs", label: "XS", cls: "w-24" },
          { id: "sm", label: "SM", cls: "w-40" },
          { id: "md", label: "MD", cls: "w-56" },
          { id: "lg", label: "LG", cls: "w-80" },
          { id: "full", label: "Full", cls: "w-full" },
        ],
        default: "theme",
      },
      {
        id: "maxw",
        label: "Max width",
        choices: [
          { id: "none", label: "None", cls: "" },
          { id: "xs", label: "XS", cls: "max-w-24" },
          { id: "sm", label: "SM", cls: "max-w-40" },
          { id: "md", label: "MD", cls: "max-w-56" },
          { id: "lg", label: "LG", cls: "max-w-80" },
          { id: "screen", label: "Screen", cls: "max-w-screen-sm" },
        ],
        default: "none",
        note: "Caps the slider inside wide rows without shrinking it below its width.",
      },
      {
        id: "pad",
        label: "Margin",
        choices: [
          { id: "none", label: "None", cls: "" },
          { id: "sm", label: "SM", cls: "m-1" },
          { id: "md", label: "MD", cls: "m-2" },
        ],
        default: "none",
      },
    ],
  },
  {
    id: "label",
    label: "Label",
    group: "Data input",
    base: "label",
    variants: [{ id: "default", label: "Inline label", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="${c}"><input type="checkbox" class="checkbox checkbox-primary" checked /> Remember me</div>`,
    note: "daisyUI's `.label` is a flex row, often wrapping a checkbox.",
  },
  {
    id: "progress",
    label: "Progress",
    group: "Feedback",
    base: "progress",
    variants: [
      { id: "default", label: "Bar", cls: "" },
      { id: "striped", label: "Striped", cls: "progress-secondary" },
    ],
    colors: COLORS,
    sizes: [],
    preview: (c) => `<progress class="${c}" value="66" max="100"></progress>`,
  },
  {
    id: "badge",
    label: "Badge",
    group: "Data display",
    base: "badge",
    variants: [
      { id: "solid", label: "Solid", cls: "" },
      { id: "soft", label: "Soft", cls: "badge-soft" },
      { id: "outline", label: "Outline", cls: "badge-outline" },
      { id: "dash", label: "Dash", cls: "badge-dash" },
    ],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) => `<span class="${c}">WR</span>`,
  },
  {
    id: "alert",
    label: "Alert",
    group: "Feedback",
    base: "alert",
    variants: [
      { id: "solid", label: "Solid", cls: "" },
      { id: "soft", label: "Soft", cls: "alert-soft" },
      { id: "outline", label: "Outline", cls: "alert-outline" },
      { id: "dash", label: "Dash", cls: "alert-dash" },
    ],
    colors: COLORS,
    sizes: [],
    preview: (c) => `<div class="${c}" role="alert">Server restarting</div>`,
  },
  {
    id: "card",
    label: "Card",
    group: "Data display",
    base: "card",
    // Surface treatment only — background, border and effects are separate
    // groups so they compose instead of overriding each other.
    variants: [
      { id: "default", label: "Filled", cls: "ds-card-style-filled" },
      { id: "soft", label: "Soft", cls: "ds-card-style-soft" },
      { id: "outline", label: "Outline", cls: "ds-card-style-outline" },
      { id: "elevated", label: "Elevated", cls: "ds-card-style-elevated" },
      { id: "glass", label: "Glass", cls: "ds-card-style-glass" },
    ],
    colors: [],
    sizes: [],
    optionGroups: [
      {
        id: "pad",
        label: "Padding",
        choices: [
          { id: "none", label: "None", cls: "ds-card-pad-none" },
          { id: "xs", label: "XS", cls: "ds-card-pad-xs" },
          { id: "sm", label: "SM", cls: "ds-card-pad-sm" },
          { id: "md", label: "MD", cls: "ds-card-pad-md" },
          { id: "lg", label: "LG", cls: "ds-card-pad-lg" },
          { id: "xl", label: "XL", cls: "ds-card-pad-xl" },
        ],
        note: "Card body padding (daisyUI --card-p); font size stays unchanged.",
        default: "md",
      },
      {
        id: "margin",
        label: "Margin",
        choices: [
          { id: "none", label: "None", cls: "" },
          { id: "sm", label: "SM", cls: "ds-card-m-sm" },
          { id: "md", label: "MD", cls: "ds-card-m-md" },
          { id: "lg", label: "LG", cls: "ds-card-m-lg" },
        ],
        default: "none",
      },
      {
        id: "bg",
        label: "Background",
        choices: [
          { id: "base-200", label: "Base 200", cls: "" },
          { id: "base-100", label: "Base 100", cls: "ds-card-bg-base-100" },
          { id: "base-300", label: "Base 300", cls: "ds-card-bg-base-300" },
          { id: "primary", label: "Primary", cls: "ds-card-bg-primary" },
          { id: "secondary", label: "Secondary", cls: "ds-card-bg-secondary" },
          { id: "accent", label: "Accent", cls: "ds-card-bg-accent" },
          { id: "neutral", label: "Neutral", cls: "ds-card-bg-neutral" },
          { id: "info", label: "Info", cls: "ds-card-bg-info" },
          { id: "success", label: "Success", cls: "ds-card-bg-success" },
          { id: "warning", label: "Warning", cls: "ds-card-bg-warning" },
          { id: "error", label: "Error", cls: "ds-card-bg-error" },
        ],
        note: "Base colour the Filled / Soft / Elevated / Glass surfaces are built from.",
        default: "base-200",
      },
      {
        id: "bdw",
        label: "Border width",
        choices: [
          { id: "auto", label: "Auto", cls: "" },
          { id: "none", label: "None", cls: "ds-card-bw-none" },
          { id: "thin", label: "Thin", cls: "ds-card-bw-thin" },
          { id: "medium", label: "Medium", cls: "ds-card-bw-medium" },
          { id: "thick", label: "Thick", cls: "ds-card-bw-thick" },
        ],
        default: "auto",
      },
      {
        id: "bds",
        label: "Border style",
        choices: [
          { id: "auto", label: "Auto", cls: "" },
          { id: "solid", label: "Solid", cls: "ds-card-bs-solid" },
          { id: "dash", label: "Dashed", cls: "ds-card-bs-dash" },
          { id: "dot", label: "Dotted", cls: "ds-card-bs-dot" },
        ],
        default: "auto",
      },
      {
        id: "bdc",
        label: "Border colour",
        choices: [
          { id: "hint", label: "Hint", cls: "" },
          { id: "content", label: "Content", cls: "ds-card-bdc-content" },
          { id: "base-300", label: "Base 300", cls: "ds-card-bdc-base" },
          { id: "primary", label: "Primary", cls: "ds-card-bdc-primary" },
          { id: "secondary", label: "Secondary", cls: "ds-card-bdc-secondary" },
          { id: "accent", label: "Accent", cls: "ds-card-bdc-accent" },
          { id: "success", label: "Success", cls: "ds-card-bdc-success" },
          { id: "warning", label: "Warning", cls: "ds-card-bdc-warning" },
          { id: "error", label: "Error", cls: "ds-card-bdc-error" },
        ],
        default: "hint",
      },
      {
        id: "radius",
        label: "Radius",
        choices: [
          { id: "md", label: "MD", cls: "" },
          { id: "none", label: "None", cls: "ds-card-r-none" },
          { id: "sm", label: "SM", cls: "ds-card-r-sm" },
          { id: "lg", label: "LG", cls: "ds-card-r-lg" },
          { id: "xl", label: "XL", cls: "ds-card-r-xl" },
          { id: "full", label: "Pill", cls: "ds-card-r-full" },
        ],
        default: "md",
      },
      {
        id: "shadow",
        label: "Shadow",
        choices: [
          { id: "none", label: "None", cls: "" },
          { id: "sm", label: "SM", cls: "ds-card-sh-sm" },
          { id: "md", label: "MD", cls: "ds-card-sh-md" },
          { id: "lg", label: "LG", cls: "ds-card-sh-lg" },
          { id: "xl", label: "XL", cls: "ds-card-sh-xl" },
          { id: "glow", label: "Glow", cls: "ds-card-sh-glow" },
        ],
        default: "none",
      },
      {
        id: "image",
        label: "Background image",
        choices: [
          { id: "none", label: "None", cls: "" },
          { id: "grid", label: "Grid", cls: "ds-card-img ds-card-img-grid" },
          { id: "dots", label: "Dots", cls: "ds-card-img ds-card-img-dots" },
          { id: "stripes", label: "Stripes", cls: "ds-card-img ds-card-img-stripes" },
          { id: "custom", label: "Custom URL", cls: "ds-card-img" },
        ],
        note: "Patterns are drawn under the card content; combine with opacity/blur.",
        default: "none",
      },
      {
        id: "imgop",
        label: "Image opacity",
        choices: [
          { id: "15", label: "15%", cls: "ds-card-imgop-15" },
          { id: "30", label: "30%", cls: "ds-card-imgop-30" },
          { id: "50", label: "50%", cls: "ds-card-imgop-50" },
          { id: "70", label: "70%", cls: "ds-card-imgop-70" },
          { id: "90", label: "90%", cls: "ds-card-imgop-90" },
        ],
        default: "50",
      },
      {
        id: "imgblur",
        label: "Image blur",
        choices: [
          { id: "0", label: "None", cls: "ds-card-imgblur-0" },
          { id: "2", label: "2px", cls: "ds-card-imgblur-2" },
          { id: "6", label: "6px", cls: "ds-card-imgblur-6" },
          { id: "12", label: "12px", cls: "ds-card-imgblur-12" },
        ],
        default: "0",
      },
    ],
    imageField: {
      label: "Image URL",
      placeholder: "https://… or /path/to/image.png",
      group: "image",
      choice: "custom",
    },
    preview: (c) =>
      `<div class="${c} text-base-content" style="width:170px"><div class="card-body"><h2 class="card-title">Map</h2><p>AMT-Freestyle2</p></div></div>`,
  },
  {
    id: "tabs",
    label: "Tabs",
    group: "Navigation",
    base: "tabs",
    variants: [
      { id: "box", label: "Box", cls: "tabs-box" },
      { id: "border", label: "Border", cls: "tabs-border" },
      { id: "lift", label: "Lift", cls: "tabs-lift" },
    ],
    colors: [],
    sizes: SIZES,
    preview: (c) =>
      `<div class="${c}"><button class="tab tab-active">Video</button><button class="tab">Audio</button></div>`,
  },
  {
    id: "menu",
    label: "Menu",
    group: "Navigation",
    base: "menu",
    variants: [
      { id: "vertical", label: "Vertical", cls: "" },
      { id: "horizontal", label: "Horizontal", cls: "menu-horizontal" },
    ],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<ul class="${c}" style="width:170px"><li><a class="menu-active">Play</a></li><li><a>Settings</a></li></ul>`,
  },
  {
    id: "divider",
    label: "Divider",
    group: "Layout",
    base: "divider",
    variants: [{ id: "default", label: "Line", cls: "" }],
    shapes: [
      { id: "plain", label: "No text", cls: "" },
      { id: "text", label: "With text", cls: "" },
      { id: "horizontal", label: "Horizontal", cls: "divider-horizontal" },
      { id: "horizontal-text", label: "Horizontal + text", cls: "divider-horizontal" },
      { id: "responsive", label: "Responsive", cls: "lg:divider-horizontal" },
    ],
    colors: COLORS,
    sizes: [],
    preview: (c, shape) => {
      const label = shape.includes("text") ? "or" : "";
      const inner = label ? label : "";
      const box = (t: string) =>
        `<span style="display:block;padding:8px 12px;background:color-mix(in oklab, var(--color-base-content) 12%, var(--color-base-100));border-radius:var(--radius-box);width:120px;text-align:center;font-size:11px">${t}</span>`;
      if (shape.startsWith("horizontal") || shape === "responsive") {
        return `<div style="display:flex;align-items:center">${box("content")}<div class="${c}">${inner}</div>${box("content")}</div>`;
      }
      return `<div style="width:200px">${box("content")}<div class="${c}">${inner}</div>${box("content")}</div>`;
    },
  },
  {
    id: "link",
    label: "Link",
    group: "Navigation",
    base: "link",
    variants: [
      { id: "default", label: "Static", cls: "" },
      { id: "hover", label: "Hover underline", cls: "link-hover" },
    ],
    colors: COLORS,
    sizes: [],
    preview: (c) => `<a class="${c}" href="#">devlog</a>`,
  },
  {
    id: "table",
    label: "Table",
    group: "Data display",
    base: "table",
    variants: [
      { id: "default", label: "Plain", cls: "" },
      { id: "zebra", label: "Zebra", cls: "table-zebra" },
      { id: "pin", label: "Pinned header", cls: "table-pin-rows" },
    ],
    colors: [],
    sizes: SIZES,
    preview: (c) =>
      `<table class="${c}" style="width:190px"><thead><tr><th>#</th><th>Time</th></tr></thead><tbody><tr><td>1</td><td>0:42.17</td></tr><tr><td>2</td><td>0:43.02</td></tr></tbody></table>`,
  },
  {
    id: "textarea",
    label: "Textarea",
    group: "Data input",
    base: "textarea",
    variants: [
      { id: "default", label: "Bordered", cls: "" },
      { id: "ghost", label: "Ghost", cls: "textarea-ghost" },
    ],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) => `<textarea class="${c}" rows="2">gg</textarea>`,
    optionGroups: [
      {
        id: "width",
        label: "Width",
        choices: [
          { id: "auto", label: "Auto", cls: "" },
          { id: "xs", label: "XS", cls: "w-24" },
          { id: "sm", label: "SM", cls: "w-40" },
          { id: "md", label: "MD", cls: "w-56" },
          { id: "lg", label: "LG", cls: "w-80" },
          { id: "full", label: "Full", cls: "w-full" },
        ],
        default: "auto",
        note: "Fixed widths use rem utilities; Full fills whatever contains it.",
      },
      {
        id: "height",
        label: "Height",
        choices: [
          { id: "auto", label: "Auto", cls: "" },
          { id: "sm", label: "SM", cls: "h-8 min-h-0" },
          { id: "md", label: "MD", cls: "h-10 min-h-0" },
          { id: "lg", label: "LG", cls: "h-12 min-h-0" },
        ],
        default: "auto",
      },
      {
        id: "pad",
        label: "Padding",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "none", label: "None", cls: "px-0 py-0" },
          { id: "sm", label: "SM", cls: "px-2 py-1" },
          { id: "md", label: "MD", cls: "px-3 py-2" },
          { id: "lg", label: "LG", cls: "px-5 py-3" },
        ],
        default: "theme",
      },
      {
        id: "margin",
        label: "Margin",
        choices: [
          { id: "none", label: "None", cls: "" },
          { id: "sm", label: "SM", cls: "m-1" },
          { id: "md", label: "MD", cls: "m-2" },
          { id: "lg", label: "LG", cls: "m-4" },
        ],
        default: "none",
      },
      {
        id: "bdw",
        label: "Border width",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "thin", label: "1px", cls: "border" },
          { id: "thick", label: "2px", cls: "border-2" },
          { id: "none", label: "None", cls: "border-0" },
        ],
        default: "theme",
      },
      {
        id: "bds",
        label: "Border style",
        choices: [
          { id: "solid", label: "Solid", cls: "" },
          { id: "dashed", label: "Dashed", cls: "border-dashed" },
          { id: "dotted", label: "Dotted", cls: "border-dotted" },
        ],
        default: "solid",
      },
      {
        id: "radius",
        label: "Radius",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "none", label: "None", cls: "rounded-none" },
          { id: "sm", label: "SM", cls: "rounded-sm" },
          { id: "md", label: "MD", cls: "rounded-md" },
          { id: "lg", label: "LG", cls: "rounded-lg" },
          { id: "full", label: "Full", cls: "rounded-full" },
        ],
        default: "theme",
        note: "Theme follows the Radius knobs; the others pin this component.",
      },
    ],
  },
  {
    id: "file-input",
    label: "File input",
    group: "Data input",
    base: "file-input",
    variants: [
      { id: "default", label: "Bordered", cls: "" },
      { id: "ghost", label: "Ghost", cls: "file-input-ghost" },
    ],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) => `<input type="file" class="${c}" />`,
    optionGroups: [
      {
        id: "width",
        label: "Width",
        choices: [
          { id: "auto", label: "Auto", cls: "" },
          { id: "xs", label: "XS", cls: "w-24" },
          { id: "sm", label: "SM", cls: "w-40" },
          { id: "md", label: "MD", cls: "w-56" },
          { id: "lg", label: "LG", cls: "w-80" },
          { id: "full", label: "Full", cls: "w-full" },
        ],
        default: "auto",
        note: "Fixed widths use rem utilities; Full fills whatever contains it.",
      },
      {
        id: "height",
        label: "Height",
        choices: [
          { id: "auto", label: "Auto", cls: "" },
          { id: "sm", label: "SM", cls: "h-8 min-h-0" },
          { id: "md", label: "MD", cls: "h-10 min-h-0" },
          { id: "lg", label: "LG", cls: "h-12 min-h-0" },
        ],
        default: "auto",
      },
      {
        id: "pad",
        label: "Padding",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "none", label: "None", cls: "px-0 py-0" },
          { id: "sm", label: "SM", cls: "px-2 py-1" },
          { id: "md", label: "MD", cls: "px-3 py-2" },
          { id: "lg", label: "LG", cls: "px-5 py-3" },
        ],
        default: "theme",
      },
      {
        id: "margin",
        label: "Margin",
        choices: [
          { id: "none", label: "None", cls: "" },
          { id: "sm", label: "SM", cls: "m-1" },
          { id: "md", label: "MD", cls: "m-2" },
          { id: "lg", label: "LG", cls: "m-4" },
        ],
        default: "none",
      },
      {
        id: "bdw",
        label: "Border width",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "thin", label: "1px", cls: "border" },
          { id: "thick", label: "2px", cls: "border-2" },
          { id: "none", label: "None", cls: "border-0" },
        ],
        default: "theme",
      },
      {
        id: "bds",
        label: "Border style",
        choices: [
          { id: "solid", label: "Solid", cls: "" },
          { id: "dashed", label: "Dashed", cls: "border-dashed" },
          { id: "dotted", label: "Dotted", cls: "border-dotted" },
        ],
        default: "solid",
      },
      {
        id: "radius",
        label: "Radius",
        choices: [
          { id: "theme", label: "Theme", cls: "" },
          { id: "none", label: "None", cls: "rounded-none" },
          { id: "sm", label: "SM", cls: "rounded-sm" },
          { id: "md", label: "MD", cls: "rounded-md" },
          { id: "lg", label: "LG", cls: "rounded-lg" },
          { id: "full", label: "Full", cls: "rounded-full" },
        ],
        default: "theme",
        note: "Theme follows the Radius knobs; the others pin this component.",
      },
    ],
  },
  {
    id: "rating",
    label: "Rating",
    group: "Data input",
    base: "rating",
    variants: [{ id: "default", label: "Stars", cls: "" }],
    colors: [],
    sizes: SIZES,
    preview: (c) =>
      `<div class="${c}"><input type="radio" class="mask mask-star-2 bg-warning" checked /><input type="radio" class="mask mask-star-2 bg-warning" /></div>`,
  },
  {
    id: "filter",
    label: "Filter",
    group: "Data input",
    base: "filter",
    variants: [{ id: "default", label: "Chip row", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="${c}"><input class="btn btn-square" type="radio" name="f" aria-label="All" checked /><input class="btn" type="radio" name="f" aria-label="Duel" /></div>`,
  },
  {
    id: "fieldset",
    label: "Fieldset",
    group: "Data input",
    base: "fieldset",
    variants: [{ id: "default", label: "Grouped fields", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<fieldset class="${c}"><legend class="fieldset-legend">Account</legend><input class="input input-sm w-full" placeholder="nickname" /></fieldset>`,
  },
  {
    id: "collapse",
    label: "Collapse / accordion",
    group: "Data display",
    base: "collapse",
    variants: [
      { id: "default", label: "Plain", cls: "" },
      { id: "arrow", label: "Arrow", cls: "collapse-arrow" },
      { id: "plus", label: "Plus", cls: "collapse-plus" },
    ],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="${c} bg-base-200" style="width:190px"><input type="checkbox" /><div class="collapse-title">Advanced</div><div class="collapse-content"><p>Body</p></div></div>`,
  },
  {
    id: "avatar",
    label: "Avatar",
    group: "Data display",
    base: "avatar",
    variants: [
      { id: "circle", label: "Circle", cls: "" },
      { id: "rounded", label: "Rounded", cls: "avatar-placeholder" },
    ],
    colors: [],
    sizes: [],
    shapes: [
      { id: "plain", label: "Plain", cls: "" },
      { id: "online", label: "Online", cls: "avatar-online" },
      { id: "offline", label: "Offline", cls: "avatar-offline" },
    ],
    preview: (c) =>
      `<div class="${c}"><div class="w-10 rounded-full bg-primary text-primary-content grid place-items-center">M</div></div>`,
  },
  {
    id: "chat",
    label: "Chat bubble",
    group: "Data display",
    base: "chat",
    colorBase: "chat-bubble",
    variants: [
      { id: "start", label: "Start", cls: "chat-start" },
      { id: "end", label: "End", cls: "chat-end" },
    ],
    colors: COLORS,
    sizes: [],
    preview: (c) => `<div class="${c}"><div class="chat-bubble">gg</div></div>`,
  },
  {
    id: "list",
    label: "List",
    group: "Data display",
    base: "list",
    variants: [
      { id: "default", label: "Plain", cls: "" },
      { id: "rows", label: "Rows", cls: "list-row" },
    ],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<ul class="${c} bg-base-200" style="width:190px"><li class="list-row"><span>AMT-Freestyle2</span><span class="badge badge-xs">WR</span></li><li class="list-row"><span>pro-q3dm6</span></li></ul>`,
  },
  {
    id: "stat",
    label: "Stat",
    group: "Data display",
    base: "stat",
    variants: [{ id: "default", label: "Stat", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="stats bg-base-200"><div class="${c}"><div class="stat-title">Time</div><div class="stat-value text-2xl">0:42.17</div><div class="stat-desc">personal best</div></div></div>`,
  },
  {
    id: "status",
    label: "Status dot",
    group: "Data display",
    base: "status",
    variants: [{ id: "default", label: "Dot", cls: "" }],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) => `<span class="${c}"></span>`,
  },
  {
    id: "timeline",
    label: "Timeline",
    group: "Data display",
    base: "timeline",
    variants: [
      { id: "vertical", label: "Vertical", cls: "timeline-vertical" },
      { id: "horizontal", label: "Horizontal", cls: "timeline-horizontal" },
    ],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<ul class="${c}" style="max-height:140px"><li><hr/><div class="timeline-start">09:00</div><div class="timeline-middle">●</div><div class="timeline-end timeline-box">Joined</div><hr/></li><li><hr/><div class="timeline-start">09:12</div><div class="timeline-middle">●</div><div class="timeline-end timeline-box">Finished</div></li></ul>`,
  },
  {
    id: "loading",
    label: "Loading",
    group: "Feedback",
    base: "loading",
    variants: [
      { id: "spinner", label: "Spinner", cls: "loading-spinner" },
      { id: "dots", label: "Dots", cls: "loading-dots" },
      { id: "ring", label: "Ring", cls: "loading-ring" },
      { id: "ball", label: "Ball", cls: "loading-ball" },
      { id: "bars", label: "Bars", cls: "loading-bars" },
      { id: "infinity", label: "Infinity", cls: "loading-infinity" },
    ],
    colors: COLORS,
    sizes: SIZES,
    preview: (c) => `<span class="${c}"></span>`,
  },
  {
    id: "skeleton",
    label: "Skeleton",
    group: "Feedback",
    base: "skeleton",
    variants: [
      { id: "text", label: "Text", cls: "" },
      { id: "circle", label: "Circle", cls: "rounded-full" },
    ],
    colors: [],
    sizes: [],
    preview: (c) => `<div class="${c} h-10 w-32"></div>`,
  },
  {
    id: "tooltip",
    label: "Tooltip",
    group: "Feedback",
    base: "tooltip",
    variants: [
      { id: "top", label: "Top", cls: "tooltip-top" },
      { id: "bottom", label: "Bottom", cls: "tooltip-bottom" },
      { id: "left", label: "Left", cls: "tooltip-left" },
      { id: "right", label: "Right", cls: "tooltip-right" },
    ],
    colors: COLORS,
    sizes: [],
    preview: (c) =>
      `<div class="${c}" data-tip="FOV"><button class="btn btn-sm">Hover</button></div>`,
  },
  {
    id: "breadcrumbs",
    label: "Breadcrumbs",
    group: "Navigation",
    base: "breadcrumbs",
    variants: [{ id: "default", label: "Trail", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="${c}"><ul><li><a>Menu</a></li><li><a>Maps</a></li><li>AMT</li></ul></div>`,
  },
  {
    id: "pagination",
    label: "Pagination",
    group: "Navigation",
    base: "join",
    variants: [{ id: "default", label: "Page buttons", cls: "" }],
    colors: [],
    sizes: SIZES,
    preview: (c) =>
      `<div class="${c}"><button class="join-item btn btn-sm">1</button><button class="join-item btn btn-sm btn-active">2</button><button class="join-item btn btn-sm">3</button></div>`,
  },
  {
    id: "steps",
    label: "Steps",
    group: "Navigation",
    base: "steps",
    colorBase: "step",
    variants: [
      { id: "horizontal", label: "Horizontal", cls: "" },
      { id: "vertical", label: "Vertical", cls: "steps-vertical" },
    ],
    colors: ["primary", "secondary", "accent", "info", "success", "warning", "error"],
    sizes: [],
    preview: (c) =>
      `<ul class="${c}"><li class="step step-primary">Qualify</li><li class="step">Race</li><li class="step">Finish</li></ul>`,
  },
  {
    id: "navbar",
    label: "Navbar",
    group: "Navigation",
    base: "navbar",
    variants: [{ id: "default", label: "Bar", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="${c} bg-base-200" style="width:230px"><div class="navbar-start"><button class="btn btn-ghost btn-xs">Menu</button></div><div class="navbar-center">webrace</div><div class="navbar-end"><button class="btn btn-ghost btn-xs">Esc</button></div></div>`,
  },
  {
    id: "dock",
    label: "Dock",
    group: "Navigation",
    base: "dock",
    variants: [{ id: "default", label: "Bottom dock", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="${c}" style="width:200px"><button class="btn btn-xs">Play</button><button class="btn btn-xs btn-active">Maps</button><button class="btn btn-xs">Opts</button></div>`,
  },
  {
    id: "join",
    label: "Join (button group)",
    group: "Layout",
    base: "join",
    variants: [
      { id: "horizontal", label: "Horizontal", cls: "" },
      { id: "vertical", label: "Vertical", cls: "join-vertical" },
    ],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="${c}"><button class="btn join-item btn-sm">Save</button><button class="btn join-item btn-sm">Load</button></div>`,
  },
  {
    id: "indicator",
    label: "Indicator",
    group: "Layout",
    base: "indicator",
    variants: [{ id: "default", label: "Badge on corner", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="${c}"><span class="indicator-item badge badge-primary badge-xs">3</span><div class="btn btn-sm">Inbox</div></div>`,
  },
  {
    id: "stack",
    label: "Stack",
    group: "Layout",
    base: "stack",
    variants: [{ id: "default", label: "Stacked cards", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="${c}"><div class="card bg-base-300 w-24 h-16"></div><div class="card bg-base-200 w-24 h-16"></div></div>`,
  },
  {
    id: "hero",
    label: "Hero",
    group: "Layout",
    base: "hero",
    variants: [{ id: "default", label: "Hero", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<div class="${c} bg-base-200" style="width:230px"><div class="hero-content text-center"><h1 class="text-lg">webrace</h1><p class="text-xs">chase your best time</p></div></div>`,
  },
  {
    id: "footer",
    label: "Footer",
    group: "Layout",
    base: "footer",
    variants: [{ id: "default", label: "Footer", cls: "" }],
    colors: [],
    sizes: [],
    preview: (c) =>
      `<footer class="${c} bg-base-200 text-xs" style="width:230px"><aside><p>webrace · v62</p></aside><nav><a>devlog</a><a>discord</a></nav></footer>`,
  },
  {
    id: "kbd",
    label: "Kbd",
    group: "Data display",
    base: "kbd",
    variants: [{ id: "default", label: "Key", cls: "" }],
    colors: [],
    sizes: SIZES,
    preview: (c) => `<kbd class="${c}">Esc</kbd>`,
  },
  {
    id: "command-palette",
    label: "Command palette",
    group: "Game UI",
    base: "palette-panel",
    variants: [{ id: "panel", label: "Panel", cls: "" }],
    colors: [],
    sizes: [],
    optionGroups: [
      {
        id: "surface",
        label: "Surface",
        default: "default",
        choices: [
          { id: "default", label: "Default", cls: "" },
          { id: "soft", label: "Soft", cls: "palette-panel--soft" },
          { id: "flat", label: "Flat", cls: "palette-panel--flat" },
        ],
      },
      {
        id: "width",
        label: "Width",
        default: "default",
        choices: [
          { id: "narrow", label: "Narrow", cls: "palette-panel--sm" },
          { id: "default", label: "Default", cls: "" },
          { id: "wide", label: "Wide", cls: "palette-panel--lg" },
        ],
      },
      {
        id: "density",
        label: "Density",
        default: "default",
        choices: [
          { id: "compact", label: "Compact", cls: "palette-panel--compact" },
          { id: "default", label: "Default", cls: "" },
          { id: "roomy", label: "Roomy", cls: "palette-panel--roomy" },
        ],
      },
    ],
    defaults: { style: "panel", options: { surface: "default", width: "default", density: "default" } },
    note:
      "The in-game command palette (default M). It reuses the settings search rows and vote actions; the panel follows the theme's panel colours, radius and fonts.",
    preview: (c) => `
      <div class="${c} comp-palette-specimen">
        <div class="settings-search-bar">
          <input value="trail" readonly />
          <span class="settings-search-hint">11 matches</span>
        </div>
        <div class="settings-search-results">
          <div class="settings-search-group">Video / Effects</div>
          <div class="settings-search-row is-selected">
            <div class="settings-search-label"><b>Player trail</b><small>How long the neon player ribbon lasts.</small></div>
            <div class="settings-search-control">
              <input type="range" min="0" max="3" step="0.1" value="0.5" />
              <input type="number" value="0.5" />
            </div>
            <button type="button" class="btn btn-ghost btn-xs btn-square">↺</button>
          </div>
          <div class="settings-search-row settings-search-action">
            <div class="settings-search-label"><b>Vote for a random map</b><small>start a map vote with a random map</small></div>
            <button type="button" class="btn btn-primary btn-xs">Run</button>
          </div>
        </div>
        <p class="palette-foot">↑↓ select · Enter open/run · Esc close</p>
      </div>`,
  },
  {
    id: "vote-action",
    label: "Vote action",
    group: "Game UI",
    base: "vote-action",
    variants: [
      { id: "outline", label: "Outline", cls: "" },
      { id: "soft", label: "Soft", cls: "vote-action--soft" },
      { id: "solid", label: "Solid", cls: "vote-action--solid" },
      { id: "ghost", label: "Ghost", cls: "vote-action--ghost" },
    ],
    colors: [],
    sizes: ["xs", "sm", "md", "lg"],
    // The game's own class names use a double dash (`vote-action--lg`), not
    // daisyUI's `base-size` pattern.
    sizeBase: "vote-action-",
    defaults: { style: "outline", size: "sm" },
    note:
      "The clickable Yes/No buttons shown under the vote text. They are a separate class from .btn so they can be themed independently; Y/N still work while pointer-locked.",
    preview: (c) => `
      <div class="vote-specimen">
        <div class="vote-specimen-text">vote: map deck16</div>
        <button type="button" class="${c} vote-action--yes">
          Yes<span class="vote-action__key">Y</span>
        </button>
        <button type="button" class="${c} vote-action--no">
          No<span class="vote-action__key">N</span>
        </button>
      </div>`,
  },
];

/** The chosen extra option for a group (falls back to the group's first choice). */
export function optionFor(group: OptionGroup, choice: StyleChoice): ComponentChoice {
  const id = choice.options?.[group.id] ?? group.default;
  return group.choices.find((c) => c.id === id) ?? group.choices[0];
}

/** Compose the class list the game should use for a component. */
export function classesFor(spec: ComponentSpec, choice: StyleChoice): string {
  const variant = spec.variants.find((v) => v.id === choice.style) ?? spec.variants[0];
  const shape = spec.shapes?.find((s) => s.id === choice.shape);
  const parts = [spec.base, variant?.cls ?? "", shape?.cls ?? ""].filter(Boolean);
  if (choice.color && spec.colors.includes(choice.color)) {
    parts.push(`${spec.colorBase ?? spec.base}-${choice.color}`);
  }
  if (choice.size && spec.sizes.includes(choice.size)) {
    parts.push(`${spec.sizeBase ?? spec.base}-${choice.size}`);
  }
  for (const group of spec.optionGroups ?? []) {
    const cls = optionFor(group, choice).cls;
    if (cls) parts.push(cls);
  }
  return parts.join(" ");
}

/** Inline custom properties a component's option groups want to set. */
export function varsFor(spec: ComponentSpec, choice: StyleChoice): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const group of spec.optionGroups ?? []) {
    Object.assign(vars, optionFor(group, choice).vars ?? {});
  }
  if (spec.imageField && choice.image) {
    vars["--ds-card-img"] = `url("${choice.image}")`;
  }
  return vars;
}

export const COMPONENT_GROUPS = Array.from(new Set(COMPONENTS.map((c) => c.group)));
